import Viewport from '../common/viewport';
import { throttle } from '../decorators';

export class ScrollableNav {
    static ELEMENT_CLASS = 'scrollable-nav';
    static INITIALIZATION_CLASS = `${ScrollableNav.ELEMENT_CLASS}--initialized`;
    static DIRECTION_VERTICAL_CLASS = `${ScrollableNav.ELEMENT_CLASS}--vertical`;
    static CONTAINER_CLASS = `${ScrollableNav.ELEMENT_CLASS}__container`;
    static ARROW_CLASS = `${ScrollableNav.ELEMENT_CLASS}__arrow`;
    static MODIFIER_PREV = 'prev';
    static MODIFIER_NEXT = 'next';
    static ELEMENT_SCROLLED_PREV_CLASS = `${ScrollableNav.ELEMENT_CLASS}--scrolled-${ScrollableNav.MODIFIER_PREV}`;
    static ELEMENT_SCROLLED_NEXT_CLASS = `${ScrollableNav.ELEMENT_CLASS}--scrolled-${ScrollableNav.MODIFIER_NEXT}`;
    static ARROW_PREV_CLASS = `${ScrollableNav.ARROW_CLASS}--${ScrollableNav.MODIFIER_PREV}`;
    static ARROW_NEXT_CLASS = `${ScrollableNav.ARROW_CLASS}--${ScrollableNav.MODIFIER_NEXT}`;

    /** @var {HTMLElement} element */
    element;

    /** @var {HTMLElement} container */
    container;

    /** @var {HTMLButtonElement[]} arrows */
    arrows = [];

    /** @var {Viewport} viewport */
    viewport;

    /** @var {{[key: string]: EventListener}} */
    eventListeners = {};

    /** @var {boolean} isVertical */
    isVertical;

    /**
     * @param {HTMLElement} element
     */
    constructor(element) {
        this.element = element;
        this.container = this.element.querySelector(`.${ScrollableNav.CONTAINER_CLASS}`);
        this.viewport = new Viewport(this.container ?? null);
        this.eventListeners.scroll = throttle(this.scroll.bind(this), 100);
        this.eventListeners.onArrowClick = this.onArrowClick.bind(this);

        this.initArrows();
        this.container.addEventListener('scroll', this.eventListeners.scroll);
        this.scroll();
        this.element.classList.add(ScrollableNav.INITIALIZATION_CLASS);
        this.isVertical = this.element.classList.contains(ScrollableNav.DIRECTION_VERTICAL_CLASS);
    }

    scroll() {
        const { viewportStart, viewportEnd, containerLength } = this.getContainerScrollParams();

        const isScrolledToPrev = viewportStart === 0;
        // Use >= instead of === because of different pixel densities on different devices
        const isScrolledToNext = Math.round(viewportEnd) >= Math.round(containerLength);

        this.element.classList[isScrolledToPrev ? 'add' : 'remove'](ScrollableNav.ELEMENT_SCROLLED_PREV_CLASS);
        this.element.classList[isScrolledToNext ? 'add' : 'remove'](ScrollableNav.ELEMENT_SCROLLED_NEXT_CLASS);
    }

    getContainerScrollParams() {
         const v = this.viewport.getViewport();

        if (this.isVertical) {
            return {
                viewportLength: v.height,
                viewportStart: v.top,
                viewportEnd: v.bottom,
                containerLength: this.container.scrollHeight,
            };
        }

        return {
            viewportLength: v.width,
            viewportStart: v.left,
            viewportEnd: v.right,
            containerLength: this.container.scrollWidth,
        };
    }

    /**
     * Scrolls backwards or forwards
     * @param {MouseEvent} event
     */
    onArrowClick(event) {
        const { viewportLength, viewportStart, viewportEnd, containerLength } = this.getContainerScrollParams();

        const arrow = event.currentTarget;
        const scrollLength = Math.round(viewportLength / 3 * 2);
        const scrollDistance = this.isPrevArrow(arrow)
            ? (-1) * Math.min(viewportStart, scrollLength)
            : Math.min(containerLength - viewportEnd, scrollLength);
        const scrollTarget = viewportStart + scrollDistance;

        this.arrows.forEach((arrow) => arrow.disabled = true);
        this.viewport.scroll(this.isVertical ? scrollTarget : null, this.isVertical ? null : scrollTarget)
            .finally(() => {
                this.arrows.forEach((arrow) => arrow.disabled = false);
            });
    }

    initArrows() {
        for (const arrowDirection of [ScrollableNav.MODIFIER_PREV, ScrollableNav.MODIFIER_NEXT]) {
            const arrow = document.createElement('button');
            arrow.className = `${ScrollableNav.ARROW_CLASS} ${ScrollableNav.ARROW_CLASS}--${arrowDirection}`;
            if (this.isPrevArrow(arrow)) {
                arrow.innerHTML = '<svg class="pi"><use xlink:href="#arrow-left"></use></svg>';
                this.element.insertBefore(arrow, this.element.children[0]);
            } else {
                arrow.innerHTML = '<svg class="pi"><use xlink:href="#arrow-right"></use></svg>';
                this.element.appendChild(arrow);
            }
            arrow.addEventListener('click', this.eventListeners.onArrowClick);
            this.arrows.push(arrow);
        }
    }

    isPrevArrow(arrow) {
        return arrow.classList.contains(ScrollableNav.ARROW_PREV_CLASS)
    }

    isNextArrow(arrow) {
        return arrow.classList.contains(ScrollableNav.ARROW_NEXT_CLASS)
    }
}